import { AUTO_DOWNLOAD_MAX_BYTES } from "@/config/constants";

export type AutoDownloadKind = "image" | "video" | "file";

export type AutoDownloadSettings = {
  autoDownloadPhotos: boolean;
  autoDownloadVideos: boolean;
  autoDownloadFiles: boolean;
};

/**
 * Pure decision: should this attachment be auto-downloaded given the settings?
 * Depends only on a config constant (no attachment middleware, no DOM/WASM), so
 * it stays trivially unit-testable.
 * - Every type has a fixed per-type size cap (`AUTO_DOWNLOAD_MAX_BYTES`) baked
 *   in config — a file above its cap simply waits for a tap. (Photos are capped
 *   too now; previously they weren't.)
 * - Videos additionally NEVER auto-download when large/streamed
 *   (`isLargeAttachment`): those play via the SW range stream, so pulling the
 *   whole blob eagerly would defeat it.
 */
export function shouldAutoDownload(
  kind: AutoDownloadKind,
  attachment: { fileSize?: number; isLargeAttachment?: boolean },
  settings: AutoDownloadSettings,
): boolean {
  const withinCap = (maxBytes: number) =>
    !attachment.fileSize || attachment.fileSize <= maxBytes;

  if (kind === "image") {
    return (
      settings.autoDownloadPhotos && withinCap(AUTO_DOWNLOAD_MAX_BYTES.image)
    );
  }
  if (kind === "video") {
    return (
      settings.autoDownloadVideos &&
      withinCap(AUTO_DOWNLOAD_MAX_BYTES.video) &&
      !attachment.isLargeAttachment
    );
  }
  return settings.autoDownloadFiles && withinCap(AUTO_DOWNLOAD_MAX_BYTES.file);
}
